/**
 * ContractValidator - Validates and corrects AI-extracted contract data
 * 
 * FIXED: Corrected buyer/seller swap detection logic
 * 
 * Key Features:
 * - Detects and fixes buyer/seller role swaps using context analysis
 * - Validates contract dates (prevents using recording dates as contract dates)
 * - Checks date chronology logic
 * - Filters boilerplate from special terms
 * - Validates property addresses
 * - Returns confidence scores, errors, warnings
 */

class ContractValidator {
  constructor(data, contractText) {
    this.data = JSON.parse(JSON.stringify(data)); // Deep clone
    this.contractText = contractText;
    this.errors = [];
    this.warnings = [];
    this.confidence = 100;
  }

  /**
   * Main validation method
   */
  validate() {
    console.log('🔍 VALIDATOR - Starting validation...');
    
    // Run all validation checks
    this.detectBuyerSellerSwap();
    this.validateContractDate();
    this.validateDateChronology();
    this.filterBoilerplateTerms();
    this.validatePropertyAddress();
    
    console.log('✅ VALIDATOR - Validation complete');
    console.log(`📊 VALIDATOR - Confidence: ${this.confidence}%`);
    
    return {
      data: this.data,
      errors: this.errors,
      warnings: this.warnings,
      confidence: this.confidence
    };
  }

  /**
   * FIXED: Detect and correct buyer/seller role swaps
   * 
   * OLD LOGIC (BROKEN):
   * - If buyer name appears in seller contexts → swap
   * 
   * NEW LOGIC (CORRECT):
   * - If buyer name appears MORE in seller contexts than buyer contexts → swap
   * - If seller name appears MORE in buyer contexts than seller contexts → swap
   */
  detectBuyerSellerSwap() {
    const buyerName = this.data.buyer_name;
    const sellerName = this.data.seller_name;
    
    if (!buyerName || !sellerName) {
      this.warnings.push('Missing buyer or seller name');
      this.confidence -= 5;
      return;
    }

    // Context patterns for buyer (person purchasing/acquiring property)
    const buyerContextPatterns = [
      `${buyerName}.*?(?:buyer|purchaser|acquiring|purchase|grantee)`,
      `(?:buyer|purchaser|acquiring|purchase|grantee).*?${buyerName}`,
      `to\\s+${buyerName}`, // "to [buyer]"
      `${buyerName}.*?agrees to buy`,
      `${buyerName}.*?agrees to purchase`
    ];

    // Context patterns for seller (person selling/conveying property)
    const sellerContextPatterns = [
      `${sellerName}.*?(?:seller|vendor|conveying|grantor)`,
      `(?:seller|vendor|conveying|grantor).*?${sellerName}`,
      `from\\s+${sellerName}`, // "from [seller]"
      `${sellerName}.*?agrees to sell`,
      `${sellerName}.*?agrees to convey`
    ];

    // Count how many times each name appears in its expected context
    let buyerInBuyerContext = 0;
    let buyerInSellerContext = 0;
    let sellerInSellerContext = 0;
    let sellerInBuyerContext = 0;

    // Check buyer name in buyer contexts
    buyerContextPatterns.forEach(pattern => {
      const regex = new RegExp(pattern, 'gi');
      const matches = this.contractText.match(regex);
      if (matches) buyerInBuyerContext += matches.length;
    });

    // Check buyer name in seller contexts  
    sellerContextPatterns.forEach(pattern => {
      const regex = new RegExp(pattern.replace(sellerName, buyerName), 'gi');
      const matches = this.contractText.match(regex);
      if (matches) buyerInSellerContext += matches.length;
    });

    // Check seller name in seller contexts
    sellerContextPatterns.forEach(pattern => {
      const regex = new RegExp(pattern, 'gi');
      const matches = this.contractText.match(regex);
      if (matches) sellerInSellerContext += matches.length;
    });

    // Check seller name in buyer contexts
    buyerContextPatterns.forEach(pattern => {
      const regex = new RegExp(pattern.replace(buyerName, sellerName), 'gi');
      const matches = this.contractText.match(regex);
      if (matches) sellerInBuyerContext += matches.length;
    });

    console.log(`🔍 VALIDATOR - Buyer "${buyerName}" context analysis:`);
    console.log(`   - In buyer contexts: ${buyerInBuyerContext}`);
    console.log(`   - In seller contexts: ${buyerInSellerContext}`);
    
    console.log(`🔍 VALIDATOR - Seller "${sellerName}" context analysis:`);
    console.log(`   - In seller contexts: ${sellerInSellerContext}`);
    console.log(`   - In buyer contexts: ${sellerInBuyerContext}`);

    // FIXED LOGIC: Swap if roles appear backwards
    const shouldSwap = (
      (buyerInSellerContext > buyerInBuyerContext) ||  // Buyer name appears more as seller
      (sellerInBuyerContext > sellerInSellerContext)   // Seller name appears more as buyer
    );

    if (shouldSwap) {
      console.log('🔄 VALIDATOR - SWAPPING buyer and seller');
      
      // Swap the names
      const temp = this.data.buyer_name;
      this.data.buyer_name = this.data.seller_name;
      this.data.seller_name = temp;
      
      console.log(`✅ VALIDATOR - NEW buyer: ${this.data.buyer_name}`);
      console.log(`✅ VALIDATOR - NEW seller: ${this.data.seller_name}`);
      
      this.warnings.push('Detected and corrected buyer/seller swap');
      this.confidence -= 5;
    } else {
      console.log('✅ VALIDATOR - Buyer/Seller roles appear correct');
    }
  }

  /**
   * Validate contract date (prevent using recording date as contract date)
   */
  validateContractDate() {
    const contractDate = this.data.dates?.contractDate?.value;
    
    if (!contractDate) {
      this.errors.push('Missing contract date');
      this.confidence -= 10;
      return;
    }

    // Check if contract text mentions "recorded" near this date
    const datePattern = contractDate.replace(/\//g, '\\/');
    const recordedPattern = new RegExp(`(?:recorded|recording|registry).*?${datePattern}|${datePattern}.*?(?:recorded|recording|registry)`, 'gi');
    
    if (recordedPattern.test(this.contractText)) {
      this.warnings.push('Contract date may actually be recording date - verify manually');
      this.confidence -= 10;
    }

    console.log(`✅ VALIDATOR - Contract date looks valid: ${contractDate}`);
  }

  /**
   * Validate date chronology
   */
  validateDateChronology() {
    const contractDate = this.data.dates?.contractDate?.value;
    const closingDate = this.data.dates?.closingDate?.value;
    
    if (!contractDate || !closingDate) {
      return; // Already flagged in validateContractDate
    }

    const contract = new Date(contractDate);
    const closing = new Date(closingDate);

    if (closing < contract) {
      this.errors.push('Closing date is before contract date');
      this.confidence -= 15;
    } else {
      console.log('✅ VALIDATOR - Date chronology checked');
    }
  }

  /**
   * Filter boilerplate terms from other_important_terms
   */
  filterBoilerplateTerms() {
    const boilerplatePatterns = [
      /time is of the essence/i,
      /as-?is condition/i,
      /property (?:is )?sold as-?is/i,
      /buyer (?:has|had) (?:the )?right to (?:conduct|perform)/i,
      /reasonable use and wear/i,
      /title (?:shall be|to be) good and (?:clear|marketable)/i,
      /free (?:from|of) liens and encumbrances/i,
      /prorations? (?:shall be made|as of)/i,
      /^inspections?$/i,
      /home inspection/i,
      /lead paint/i,
      /smoke (?:detectors?|alarms?)/i,
      /carbon monoxide/i,
      /building permits/i,
      /building codes?/i,
      /zoning (?:laws?|bylaws?|ordinances?)/i,
      /all fixtures and fittings/i,
      /escrow agent/i,
      /^deposits?$/i,
      /earnest money/i
    ];

    if (this.data.other_important_terms && Array.isArray(this.data.other_important_terms)) {
      const before = this.data.other_important_terms.length;
      
      this.data.other_important_terms = this.data.other_important_terms.filter(term => {
        return !boilerplatePatterns.some(pattern => pattern.test(term));
      });
      
      const after = this.data.other_important_terms.length;
      const filtered = before - after;
      
      if (filtered > 0) {
        console.log(`🗑️ VALIDATOR - Filtered ${filtered} boilerplate terms`);
      }
    }
  }

  /**
   * Validate property address
   */
  validatePropertyAddress() {
    const property = this.data.property;
    
    if (!property || !property.address) {
      this.errors.push('Missing property address');
      this.confidence -= 15;
      return;
    }

    const address = property.address;
    
    // Check for required components
    const hasStreet = /\d+\s+\w+/.test(address); // Number + street name
    const hasCity = property.city && property.city.length > 0;
    const hasState = property.state && property.state.length === 2;
    const hasZip = property.zip && /\d{5}/.test(property.zip);
    
    if (!hasStreet) {
      this.errors.push('Property address missing street number/name');
      this.confidence -= 10;
    }
    
    if (!hasCity) {
      this.warnings.push('Property address missing city');
      this.confidence -= 5;
    }
    
    if (!hasState) {
      this.warnings.push('Property address missing state');
      this.confidence -= 5;
    }
    
    if (!hasZip) {
      this.warnings.push('Property address missing ZIP code');
      this.confidence -= 5;
    }

    if (hasStreet && hasCity && hasState && hasZip) {
      console.log('✅ VALIDATOR - Property address complete');
    }
  }
}

export default ContractValidator;